import NodeEvents from 'events';
import { KNOWLEDGE_VIEW_EDIT_SESSION_CHANGED } from '../helpers/knowledgeViewEditSession';
import { loadAssetInformationPanelOpen, saveAssetInformationPanelOpen } from '../helpers/assetInformationPanelSession';
import { PositionDataModel } from '@syncfusion/ej2-react-popups';
import * as Inovx from '@shared';
import ViewerConstants from '../constants/ViewerConstants';
import PanoViewerConstants from '../constants/PanoViewerConstants';
import AssetSetVisualizationStore from './AssetSetVisualizationStore';
import { IHierarchyItemDetail, defaultContentLayers, panoContentLayers, ContentLayers, KNOWLEDGE_VIEW_CONTENT_LAYERS_VERSION } from '../Interfaces/ViewerInterfaces';
import * as Interfaces from '../Interfaces/Interfaces';
import * as Enumerations from '../Enumerations/Enumerations';

type ViewerCameraProps = {
    yaw: number;
    pitch: number;
    fov: number;
    position: IVector3D | null;
};

type Viewer3DCameraProps = {
    position: IVector3D | null;
    target: IVector3D | null;
};

const DEFAULT_DISPLAY_OBSTRUCTED_TAGS = true;
const DEFAULT_FILTER_OVERLAPPING_360_POSITIONS = true;

class ViewerStore extends NodeEvents.EventEmitter/* implements IViewerStore*/ {
    private static MEASUREMENT_ID_COUNTER = 1;
    private static readonly CHANGE_EVENT = 'Viewer.change'
    private _viewerRuntimeSettings: Record<string, any> = {};
    private _allowMultiSelect: boolean = false;
    private _show3DViewer: boolean = false;
    private _lodData: string = "";
    private _rangeViewDistance: number = 0;
    private _overrideFocalDistance: number = 0;
    private _displayObstructedTags: boolean = DEFAULT_DISPLAY_OBSTRUCTED_TAGS;
    private _filterOverlapping360Positions: boolean = DEFAULT_FILTER_OVERLAPPING_360_POSITIONS;
    private _selectedViewImage: IView360Detail = null;
    private _viewOntology: IOntologyPart = null;
    private _selectedAssets: string[] = [];
    private _selectedAssemblyBounds: IAssemblyBounds = null;
    private _selectedAnnotations: string[] = [];
    private _selectedHierarchyItems: IHierarchyItemDetail[] = [];
    private _selectedMeshItem: any = null;
    private _selectedAssetSet: IAssetSet = null;
    private _selectedAssembly: string = null;
    private _updatedAnnotation: IAnnotationDisplayItem = null;
    private _filteredAssets: string[] = [];
    private _visibleAssetTagClasses: string[] | null = null;
    private _displayAllAssetTags = false;
    private _occludedAnnotations: string[] = [];
    private _measurements: Measurement[] = [];
    private _assemblies: IAssembly[] = [];
    private _focusedAnnotation: IAnnotationDisplayItem = null;
    private _focusedHierarchyItemID: {id: string} = null;
    private _focusedMeasurement: Measurement = null;
    private _selectedKnowledgeView: IKnowledgeViewDetail = null;
    private _committedKnowledgeView: IKnowledgeViewDetail = null;
    private _savedKnowledgeView: string = null;
    private _savedAssetSetAppearances: { id: string; color?: number; materialID?: string | null }[] = [];
    private _depthMapLoaded: boolean = false;
    private _isMiniMapOpen: boolean = false;
    private _miniMapPositions: PositionDataModel = null;
    private _miniMapSize: {width: number, height: number} = { width: 350, height: 300 };
    private _miniMapFullScreen: boolean = false;
    private _displayMode: DisplayMode = null;
    private _annotationType: AnnotationType = null;
    private _selectedAnnotationClassID: string = null;
    private _measurementType: MeasurementType = null;
    private _assemblyCreateID: string = null;
    private _assemblyCreationTemplate: IAssemblyTemplate = null;
    private _tagsVersion: number = 0;
    private _kvContentVersion: number = 0;
    private _knowledgeViewOpenVersion: number = 0;
    private _defaultSettings: IFacilityConfig = { 
        PanoCompassVisible: true, 
        PanoScanSphereColor: '#00FFFB', 
        PanoImageSphereColor: '#F59300',
        TagVolumeBoxColor: '#00ff007f', 
        UnitMeasurement: 'mm', 
        ZoomSliderVisible: true, 
        EdgeShader: false, 
        MemoryProfile: "Medium",
        ViewerMemoryBudget: "High",
        MeshResolution: "Medium",
        MeasurementColor: '#00ff00', 
        BackgroundColor: '#C7DAE8',
        CameraType: "CameraExamine",
        ShowCrosshair: false,
        RotationSensitivity: "0",
        VelocitySensitivity: "0",
        InvertY: false
    };
    private _settings: IFacilityConfig;
    private _isCameraDirty: boolean = false;
    private _isKnowledgeViewDirty: boolean = false;
    private _knowledgeViewEditID: string | null = null;
    private _knowledgeViewEditBusy = false;
    private _isKnowledgeViewDisplayPriorityDirty: boolean = false;

    // use the type and initialize properly
    private _viewerCameraProperties: ViewerCameraProps = {
        yaw: 0,
        pitch: 0,
        fov: 0,
        position: null
    };

    private _rangePosition: IVector3D | null;
    private _rangeView: IView360Detail | null = null;
    private _viewer3DCameraProperties: Viewer3DCameraProps = { position: null, target: null };

    private _annotationTextEditorMode: boolean = false;
    private _attributesInformation: IAttributeSetValue[] = null;
    private _editedAssetTag: IAssetTag = null;
    private _openSpeedDial: string = "";
    private _openPointCloud: boolean = false;
    private _pointCloudLoaded: boolean = false;
    private _isDirectionUpdated: boolean = false;
    private _updateDirectionSource: any = 0;
    private _pointCloudData: BABYLON.Mesh[] = [];
    private _cameraDirection: BABYLON.Vector3;
    private _viewerContentTypes: string[] = [];
    private _assetTagFilter: IAssetFilter = {
        NameFilter: '',
        AssetClassFilter: [],
        PlacementStatusFilter: "All",
        QueryRules: null,
        QueryApiRules: null,
        QueryOntologyID: null
    };
   
   private _unitConversions: IUnitInformation[] = [
    { "Unit": "mm", "Conversion": 1.0, "Precision": 0, "ConversionToMM": 1.0 },
    { "Unit": "cm", "Conversion": 0.1, "Precision": 1, "ConversionToMM": 10.0 },
    { "Unit": "m", "Conversion": 0.001, "Precision": 3, "ConversionToMM": 1000.0 },
    { "Unit": "km", "Conversion": 0.000001, "Precision": 6, "ConversionToMM": 1000000.0 },
    { "Unit": "in", "Conversion": 0.03937007874015748, "Precision": 2, "ConversionToMM": 25.4 }, // Corrected factor
    { "Unit": "ft", "Conversion": 0.003280839895013123, "Precision": 3, "ConversionToMM": 304.8 }, // Corrected factor
    { "Unit": "yd", "Conversion": 0.0010936132983377078, "Precision": 4, "ConversionToMM": 914.4 }, // Corrected factor
    { "Unit": "mi", "Conversion": 0.0000006213711922373339, "Precision": 7, "ConversionToMM": 1609344.0 } // Corrected factor
];
    // Session preference; selection changes only affect the rendered panel.
    private _assetPanelVisibility: boolean = loadAssetInformationPanelOpen();
    private _selectedAssemblyTemplate: IAssemblyTemplate = null;
    private _isAssemblyDirty: boolean = false;
    private _contentLayers: ContentLayers = { ...defaultContentLayers };

    constructor() {
        super();
        this.setMaxListeners(20);
        Inovx.Shared.Flux.AppDispatcher.register(this.handleActions.bind(this));
        this._settings = this._defaultSettings;
        let displayConfiguration = AssetSetVisualizationStore.getConfiguration();
        AssetSetVisualizationStore.addChangeListener(() => {
            const next = AssetSetVisualizationStore.getConfiguration();
            if (next === displayConfiguration) return;
            displayConfiguration = next;
            if (this._displayAllAssetTags) {
                this._displayAllAssetTags = false;
                this.emitChange();
            }
        });
    }

    handleActions(payload: any) {
        switch (payload.actionType) {
            case ViewerConstants.VIEWER_CONTENT_TYPES:
                this._setViewerContentTypes(payload.data);
                break;
            case ViewerConstants.UPDATE_VIEWER_DIRECTION:
                this._setViewerDirection(payload.data.source, payload.data.direction);
                break;
            case ViewerConstants.VIEWER_SET_POINT_CLOUD_DATA:
                this._setPointCloudData(payload.data);
                break;
            case ViewerConstants.VIEWER_SET_OPEN_POINT_CLOUD:
                this._setOpenPointCloud(payload.data.open, payload.data.resetDirectionSource);
                break;
            case ViewerConstants.VIEWER_SET_POINT_CLOUD_LOADED:
                this._setPointCloudLoaded(payload.data);
                break;
            case ViewerConstants.VIEWER_SET_SPEEDDIAL:
                this._setOpenSpeedDial(payload.data);
                break;
            case ViewerConstants.VIEWER_SHOW_LOD:
                this._showLODView(payload.data, payload.distanceThreshold, payload.rangePosition, payload.overrideFocalDistance, payload.resetCamera, payload.rangeView, payload.preserveContentLayerPresentation);
                break;
            case ViewerConstants.VIEWER_SHOW_IMAGE:
                this._showViewerImage(payload.data.view, payload.data.focusedAnnotation);
                break;
            case ViewerConstants.VIEWER_SHOW_ONTOLOGY_3D:
                this._showViewerOntology(payload.data.ontology);
                break;
            case ViewerConstants.VIEWER_HIDE_IMAGE:
                this._hideViewerImage();
                break;
            case ViewerConstants.VIEWER_RESET_CAMERA_PROPERTIES:
                this._resetCameraProperties();
                break;
            case ViewerConstants.KNOWLEDGE_VIEW_OPEN:
                this._openKnowledgeView(payload.data.kv, payload.data.lodData);
                break;
            case KNOWLEDGE_VIEW_EDIT_SESSION_CHANGED:
                this._knowledgeViewEditID = payload.data.id;
                this._knowledgeViewEditBusy = payload.data.busy;
                break;
            case ViewerConstants.KNOWLEDGE_VIEW_CLOSE:
                this._closeKnowledgeView(payload.data.saveForRestore);
                break;
            case ViewerConstants.KNOWLEDGE_VIEW_SET_DATA:
                this._setSelectedKnowledgeViewData(payload.data);
                break;
            case ViewerConstants.KNOWLEDGE_VIEW_RESET_DISPLAY_PRIORITIES:
                this._resetKnowledgeViewDisplayPriorities();
                break;
            case ViewerConstants.KNOWLEDGE_VIEW_SET_DIRTY:
                this._setKnowledgeViewDirty(payload.data);
                break;
            case ViewerConstants.VIEWER_SET_DISPLAY_MODE:
                this._changeDisplayMode(payload.data.mode, payload.data.annotationType, payload.data.measurementType, payload.data.assemblyID, payload.data.annotationClassID);
                break;
            case ViewerConstants.VIEWER_SET_ASSEMBLY_CREATION_TEMPLATE:
                this._setAssemblyCreationTemplate(payload.data);
                break;
            case ViewerConstants.VIEWER_SET_MEASUREMENT_TYPE:
                this._changeMeasurementType(payload.data);
                break;
            case ViewerConstants.VIEWER_ADD_ANNOTATION:
                this._addAnnotation(payload.data);
                break;
            case ViewerConstants.VIEWER_ADD_MEASUREMENT:
                this._addMeasurement(payload.data);
                break;
            case ViewerConstants.VIEWER_ADD_ASSEMBLY:
                this._addAssembly(payload.data);
                break;
            case ViewerConstants.VIEWER_REMOVE_ASSEMBLY:
                this._removeAssembly(payload.data);
                break;
            case ViewerConstants.VIEWER_ADD_ASSEMBLIES:
                this._addAssemblies(payload.data);
                break;
            case ViewerConstants.VIEWER_SET_ASSEMBLIES:
                this._setAssemblies(payload.data);
                break;
            case ViewerConstants.VIEWER_EDIT_ANNOTATION:
                this._editAnnotation(payload.data);
                break;
            case ViewerConstants.VIEWER_DELETE_ANNOTATION:
                this._deleteAnnotation(payload.data);
                break;
            case ViewerConstants.VIEWER_SELECT:
                this._selectAsset(payload.data);
                break;
            case ViewerConstants.VIEWER_SELECT_ANNOTATIONS:
                this._selectAnnotations(payload.data);
                break;
            case ViewerConstants.VIEWER_SELECT_ASSEMBLY:
                this._selectAssembly(payload.data.assemblyID);
                this._updateAssemblyBounds(payload.data.boundingInfo);
                break;
            case ViewerConstants.VIEWER_UPDATE_ANNOTATION:
                this._updateAnnotation(payload.data);
                break;
            case ViewerConstants.VIEWER_UPDATE_SELECTED_ASSEMBLY_BOUNDS:
                this._updateAssemblyBounds(payload.data);
                break;
            case ViewerConstants.VIEWER_UPDATE_ASSEMBLY:
                this._updateAssembly(payload.data.assemblyID, payload.data.position, payload.data.rotation, payload.data.scale);
                break;
            case ViewerConstants.VIEWER_DEPTH_LOADED:
                this._setDepthMapLoaded(payload.data);
                break;
            case ViewerConstants.VIEWER_MINI_MAP_OPEN:
                this._setMiniMapOpen(payload.data);
                break;
            case ViewerConstants.VIEWER_UPDATE_SETTING:
                this._updateViewerSetting(payload.data.name, payload.data.value);
                break;
            case ViewerConstants.VIEWER_SET_MULTIPLE_SELECTION_ENABLED:
                this._setAllowMultiSelect(payload.data);
                break;
            case ViewerConstants.VIEWER_MINI_MAP_POSITIONS:
                this._setMiniMapPositions(payload.data);
                break;
            case ViewerConstants.VIEWER_MINI_MAP_SIZE:
                this.setMiniMapSize(payload.data);
                break;
            case ViewerConstants.VIEWER_MINI_MAP_FULL_SCREEN:
                this.setMiniMapFullScreen(payload.data);
                break;
            case ViewerConstants.VIEWER_FOCUS_ANNOTATION:
                this._focusAnnotation(payload.data.annotation);
                break;
            case ViewerConstants.VIEWER_FOCUS_HIERARCHY_ITEM:
                this._focusHierarchyItem(payload.data.id);
                break;
            case ViewerConstants.VIEWER_FOCUS_MEASUREMENT:
                this._focusMeasurement(payload.data.measurement);
                break;
            case ViewerConstants.VIEWER_RELOAD_MEASUREMENT:
                this._reloadMeasurement(payload.data.oldid, payload.data.measurement);
                break;
            case ViewerConstants.VIEWER_UPDATE_MEASUREMENTS:
                this._updateMeasurements(payload.data);
                break;
            case ViewerConstants.VIEWER_RECEIVE_SETTINGS:
                this._loadSettings(payload.data);
                break;
            case ViewerConstants.VIEWER_SET_RANGE_POSITION:
                this._setRangePosition(payload.data.position);
                return true; // !!! no emitChange() here
            case ViewerConstants.VIEWER_EXIT_RANGE_VIEW:
                this._exitRangeView();
                break;
            case ViewerConstants.VIEWER_SET_CAMERA_PROPERTIES:
                this._setViewerCameraProperties(payload.data.yaw, payload.data.pitch, payload.data.fov, this._selectedViewImage?.Position);
                this._setRangePosition(this._selectedViewImage?.Position);
                return true;
                
            case ViewerConstants.VIEWER_SET_3DCAMERA_PROPERTIES:
                this._set3DViewerCameraProperties(payload.data.position, payload.data.target);
                return true; // !!! no emitChange() here
            case ViewerConstants.VIEWER_SET_DISPLAY_OBSTRUCTED_TAGS:
                this._setDisplayObstructedTags(payload.data);
                break;
            case ViewerConstants.VIEWER_SET_FILTER_OVERLAPPING_360_POSITIONS:
                this._setFilterOverlapping360Positions(payload.data);
                break;
            case ViewerConstants.VIEWER_SET_VISIBLE_ASSET_TAG_CLASSES:
                this._setVisibleAssetTagClasses(payload.data);
                break;
            case ViewerConstants.VIEWER_DISPLAY_ALL_ASSET_TAGS:
                this._displayAllAssetTags = true;
                break;
            case ViewerConstants.CAMERA_SET_DIRTY:
                this._setCameraDirty(payload.data);
                break;
            case ViewerConstants.SET_ANNOTATION_EDITOR_MODE:
                this._setAnnotationEditorMode(payload.data);
                break;
            case ViewerConstants.VIEWER_RESET_ANNOTATION:
                this._resetSelectedAnnotation();
                break;
            case ViewerConstants.SET_ATTRIBUTES_INFORMATION:
                this._setAttributesInformation(payload.data);
                break;
            case ViewerConstants.VIEWER_EDIT_ASSET_TAG:
                this._editAssetTag(payload.data);
                break;
            case ViewerConstants.ASSET_TAGS_FILTER:
                this._setAssetTagFilter(payload.data.filterData, payload.data.filteredAssetIDs);
                break;
            case ViewerConstants.SET_SELECTED_ASSEMBLY_TEMPLATE:
                this._setSelectedAssemblyTemplate(payload.data);
                break;
            case ViewerConstants.SET_CONTENT_LAYERS:
                this._setContentLayers(payload.data);
                break;
            case ViewerConstants.ASSET_PANEL_VISIBILITY:
                this._changeAssetPanelVisibility(payload.data);
                break;
            case ViewerConstants.ASSEMBLY_SET_DIRTY:
                this.setAssemblyDirty(payload.data);
                return true;
            case ViewerConstants.UNDO_ASSEMBLY:
                this._undoAssembly(payload.data);
                break;
            case ViewerConstants.UPDATE_ASSEMBLIES:
                this._updateAssembies(payload.data);
                break;
            case ViewerConstants.HIERARCHYITEM_SELECT:
                this._selectHierarchyItem(payload.data);
                break;
            case ViewerConstants.HIERARCHYITEMS_SELECT:
                this._selectHierarchyItems(payload.data);
                break;
            case ViewerConstants.MESHITEM_SELECT:
                this._selectMeshItem(payload.data);
                break;
            case ViewerConstants.ASSET_SET_SELECT:
                this._selectAssetSet(payload.data);
                break;
            case ViewerConstants.KNOWLEDGE_VIEW_RENAME:
                this._renameKnowledgeView(payload.data);
                break;
                
            default:
                return true;
        }

        this.emitChange();

        return true;
    }

    private _resetCameraProperties() {
        this._resetContentLayerPresentation(defaultContentLayers);
        this._viewerCameraProperties = {
            yaw: 0,
            pitch: 0,
            fov: 0,
            position: null
        };
    }

    private _resetContentLayerPresentation(contentLayers: ContentLayers) {
        this._displayAllAssetTags = false;
        this._contentLayers = { ...contentLayers };
        this._visibleAssetTagClasses = null;
        this._displayObstructedTags = DEFAULT_DISPLAY_OBSTRUCTED_TAGS;
        this._filterOverlapping360Positions = DEFAULT_FILTER_OVERLAPPING_360_POSITIONS;
    }

    private _resetViewerContent() {
        this._displayAllAssetTags = false;

        this._show3DViewer = false;
        this._lodData = "";
        this._rangeViewDistance = 0;
        this._rangePosition = null;
        this._rangeView = null;
        this._selectedViewImage = null;
        this._openPointCloud = false;
        this._viewOntology = null;
        //this._filteredAssets = [];
        this._occludedAnnotations = [];
        this._measurements = [];
        this._selectedAssetSet = null;
        this._allowMultiSelect = false;
        // Assemblies are relevant to all viewers, so do not clear assembly content
        // Keep your eye on this, as while it means assemblies don't need to be reloaded,
        // it might introduce cases where this clear is needed.
        // Note that while navigating to a new view, the assemblies do get updated anyway, and the viewer
        // removes, and updates all the assemblies anyway.
        //this._assemblies = [];
        this._viewerRuntimeSettings = {};
        this._focusedAnnotation = null;
        this._focusedMeasurement = null;
        this._updatedAnnotation = null;
        this._selectedAssets = [];
        this._selectedAnnotations = [];
        this._selectedHierarchyItems = [];
        this._focusedHierarchyItemID = null;
        this._selectedMeshItem = null;
        this._selectedAssembly = null;
        this._selectedAssemblyBounds = null;
        this._savedAssetSetAppearances = [];

        this._selectedKnowledgeView = null;
        this._committedKnowledgeView = null;
        this._knowledgeViewEditID = null;

        this._displayMode = DisplayMode.Navigation;
        this._depthMapLoaded = false;
        this._annotationType = null;
        this._tagsVersion++;
        this._measurementType = null;
        this._assemblyCreateID = null;
        this._assemblyCreationTemplate = null;
        this._kvContentVersion++;
        this._isCameraDirty = false;
        this._isKnowledgeViewDirty = false;
        this._isKnowledgeViewDisplayPriorityDirty = false;
    }

    private _showViewerImage(view: IView360Detail, focusedAnnotation: IAnnotationDisplayItem) {
        const preserveContentLayerPresentation = !!this._selectedViewImage && !this._selectedKnowledgeView;
        const currentContentLayers = { ...this._contentLayers };
        const currentVisibleAssetTagClasses = this._visibleAssetTagClasses ? [...this._visibleAssetTagClasses] : null;
        const currentDisplayObstructedTags = this._displayObstructedTags;
        const currentFilterOverlapping360Positions = this._filterOverlapping360Positions;

        this._resetCameraProperties();
        this._resetViewerContent();
        if (preserveContentLayerPresentation) {
            this._contentLayers = currentContentLayers;
            this._visibleAssetTagClasses = currentVisibleAssetTagClasses;
            this._displayObstructedTags = currentDisplayObstructedTags;
            this._filterOverlapping360Positions = currentFilterOverlapping360Positions;
        } else {
            this._resetContentLayerPresentation(panoContentLayers);
        }

        this._selectedViewImage = view;
        this._focusedAnnotation = focusedAnnotation;
    }

    private _hideViewerImage() {
        this._resetViewerContent();
        this._resetCameraProperties();
    }

    private _showViewerOntology(ontology: IOntologyPart) {
        this._resetViewerContent();
        this._viewOntology = ontology;
    }

    private _showLODView(lodData: string, distanceThreshold: number, rangePosition: IVector3D, overrideFocalDistance: number, resetCamera: boolean, rangeView: IView360Detail, preserveContentLayerPresentation: boolean) {
        const currentContentLayers = { ...this._contentLayers };
        const currentVisibleAssetTagClasses = this._visibleAssetTagClasses ? [...this._visibleAssetTagClasses] : null;
        const currentDisplayObstructedTags = this._displayObstructedTags;
        const currentFilterOverlapping360Positions = this._filterOverlapping360Positions;

        if (resetCamera) {
            this._resetCameraProperties();
        }
        this._resetViewerContent();
        if (preserveContentLayerPresentation) {
            this._contentLayers = currentContentLayers;
            this._visibleAssetTagClasses = currentVisibleAssetTagClasses;
            this._displayObstructedTags = currentDisplayObstructedTags;
            this._filterOverlapping360Positions = currentFilterOverlapping360Positions;
        } else {
            this._resetContentLayerPresentation(defaultContentLayers);
        }

        this._lodData = lodData;
        this._show3DViewer = true;
        // this._isMiniMapOpen = false;

        this._rangeViewDistance = distanceThreshold;
        this._rangePosition = rangePosition;
        this._rangeView = rangeView;
        this._overrideFocalDistance = overrideFocalDistance;
    }

    private _openKnowledgeView(kv: IKnowledgeViewDetail, lodData?: string) {
        const editID = this._knowledgeViewEditID === kv.ID ? kv.ID : null;
        this._knowledgeViewOpenVersion++;
        if (kv.Type === 1) {
            let savedOccludedAnnotations: string[];
            let savedFilteredAssets: string[];
            let savedSelectedAssets: string[];
            let savedSelectedAnnotations: string[];
            let savedHierarchyItems: IHierarchyItemDetail[];
            let savedMeshItem: any = null;
            let savedUpdatedAnnotation: IAnnotationDisplayItem;
            let savedDepthMapLoaded: boolean;

            if (this._selectedViewImage?.ID == kv.View.ID) {
                savedOccludedAnnotations = this._occludedAnnotations;
                savedDepthMapLoaded = this._depthMapLoaded;
            }

            if (this._selectedKnowledgeView?.ID == kv.ID) {
                savedFilteredAssets = this._filteredAssets;
                savedSelectedAssets = this._selectedAssets;
                savedSelectedAnnotations = this._selectedAnnotations;
                savedUpdatedAnnotation = this._updatedAnnotation;
                savedHierarchyItems = this._selectedHierarchyItems;
                savedMeshItem = this._selectedMeshItem;
            }

            this._resetViewerContent();
            this._selectedViewImage = kv.View;
            this._viewerCameraProperties = { yaw: kv.DisplayProperties?.Yaw, pitch: kv.DisplayProperties?.Pitch, fov: kv.DisplayProperties?.Fov, position: kv.View.Position };
            this._displayObstructedTags = typeof kv?.DisplayProperties?.DisplayObstructedTags === 'boolean'
                ? kv.DisplayProperties.DisplayObstructedTags
                : DEFAULT_DISPLAY_OBSTRUCTED_TAGS;
            this._filterOverlapping360Positions = typeof kv?.DisplayProperties?.FilterOverlapping360Positions === 'boolean'
                ? kv.DisplayProperties.FilterOverlapping360Positions
                : DEFAULT_FILTER_OVERLAPPING_360_POSITIONS;
            this._restoreKnowledgeViewContentLayers(kv?.DisplayProperties, panoContentLayers);
            this._visibleAssetTagClasses = kv?.DisplayProperties?.VisibleAssetTagClasses
                ? [...kv.DisplayProperties.VisibleAssetTagClasses]
                : null;

            if (savedOccludedAnnotations) {
                this._occludeAnnotationIDs(savedOccludedAnnotations);
            }
            if (savedFilteredAssets) {
                this._filteredAssets = savedFilteredAssets;
            }
            if (savedSelectedAssets) {
                this._selectedAssets = savedSelectedAssets;
            }
            if (savedSelectedAnnotations) {
                this._selectedAnnotations = savedSelectedAnnotations;
            }
            if (savedUpdatedAnnotation) {
                this._updatedAnnotation = savedUpdatedAnnotation;
            }
            if (savedHierarchyItems) {
                this._selectedHierarchyItems = savedHierarchyItems;
            }
            if (savedMeshItem) {
                this._selectedMeshItem = savedMeshItem;
            }
            if (savedDepthMapLoaded) {
                this._depthMapLoaded = true;
            }
        } 
        else {
            let savedLodData: string = this._lodData;
            this._resetViewerContent();
            this._show3DViewer = true;
            this._lodData = lodData ?? savedLodData;

            this._restoreKnowledgeViewContentLayers(kv?.DisplayProperties);
            this._visibleAssetTagClasses = kv?.DisplayProperties?.VisibleAssetTagClasses
                ? [...kv.DisplayProperties.VisibleAssetTagClasses]
                : null;
            this._displayObstructedTags = typeof kv?.DisplayProperties?.DisplayObstructedTags === 'boolean'
                ? kv.DisplayProperties.DisplayObstructedTags
                : DEFAULT_DISPLAY_OBSTRUCTED_TAGS;
            this._filterOverlapping360Positions = typeof kv?.DisplayProperties?.FilterOverlapping360Positions === 'boolean'
                ? kv.DisplayProperties.FilterOverlapping360Positions
                : DEFAULT_FILTER_OVERLAPPING_360_POSITIONS;

            if (kv?.DisplayProperties) {
                if (kv?.DisplayProperties.ViewerSettings) {
                    this._viewerRuntimeSettings = kv?.DisplayProperties?.ViewerSettings;
                }
                else {
                    this._viewerRuntimeSettings = {};
                }
            }
        }

        this._selectedKnowledgeView = kv;
        this._knowledgeViewEditID = editID;
        this._committedKnowledgeView = this._cloneKnowledgeView(kv);
        this._savedKnowledgeView = null;
    }

    private _restoreKnowledgeViewContentLayers(displayProperties?: IDisplayProperties, fallbackContentLayers: ContentLayers = defaultContentLayers) {
        const contentLayers = displayProperties?.ContentLayers;
        if (!contentLayers) {
            this._contentLayers = { ...fallbackContentLayers };
            return;
        }

        let newContentLayers: ContentLayers = { ...defaultContentLayers };

        (Object.keys(newContentLayers) as (keyof ContentLayers)[]).forEach(layer => {
            newContentLayers[layer] = !!contentLayers.find(l => l === layer);
        });

        const isVersioned = (displayProperties.ContentLayersVersion ?? 0) >= KNOWLEDGE_VIEW_CONTENT_LAYERS_VERSION;
        if (!isVersioned) {
            const hasScanSubtypeLayers = contentLayers.some(layer =>
                layer === 'colorScanPositions' || layer === 'blackAndWhiteScanPositions');

            if (!hasScanSubtypeLayers) {
                const legacyScanPositionsVisible = contentLayers.includes('scanPositions');
                newContentLayers.colorScanPositions = legacyScanPositionsVisible;
                newContentLayers.blackAndWhiteScanPositions = legacyScanPositionsVisible;
            }

            if (!contentLayers.includes('positions360')
                && contentLayers.some(layer => layer === 'scanPositions' || layer === 'imagePositions')) {
                newContentLayers.positions360 = true;
            }
        }

        this._contentLayers = newContentLayers;
    }

    private _closeKnowledgeView(saveForRestore: boolean) {
        this._knowledgeViewEditID = null;
        if (saveForRestore && this._selectedKnowledgeView?.ID) {
            this._savedKnowledgeView = this._selectedKnowledgeView?.ID;
        }

        if (this._selectedKnowledgeView) {
            const fallbackContentLayers = this._selectedKnowledgeView.Type === 1
                ? panoContentLayers
                : defaultContentLayers;
            this._resetContentLayerPresentation(fallbackContentLayers);
        }

        this._selectedKnowledgeView = null;
        this._committedKnowledgeView = null;
        this._savedAssetSetAppearances = [];
    this._selectedAssets = [];
    this._selectedAnnotations = [];
    this._selectedHierarchyItems = [];
        this._selectedMeshItem = null;
        this._selectedAssembly = null;
        this._selectedAssemblyBounds = null
        this._updatedAnnotation = null;
        this._tagsVersion++;
        this._kvContentVersion++;
        this._isCameraDirty = false;
        this._isKnowledgeViewDirty = false;
        this._isKnowledgeViewDisplayPriorityDirty = false;
        this._viewerRuntimeSettings = {};
    }

    private _setSelectedKnowledgeViewData(data: {kv: IKnowledgeView, kvDetail: IKnowledgeViewDetail}) {
        const kvId = data.kv?.ID || data.kvDetail?.ID;
        if (this._selectedKnowledgeView?.ID !== kvId) {
            console.warn("Mismatched Knowledge View IDs in setSelectedKnowledgeViewData");
            return;
        }

        if (data.kvDetail) {
            this._displayAllAssetTags = false;
            const currentAssetSets = this._selectedKnowledgeView.AssetSets ?? [];
            const newAssetSets = data.kvDetail.AssetSets?.map(assetSet => {
                const currentAppearance = currentAssetSets.find(entry => entry.ID === assetSet.ID);
                if (assetSet.Ord < 0) { // Asset set with negative Ord is meant to be removed
                    const savedAppearance = this._savedAssetSetAppearances.find(entry => entry.id === assetSet.ID);
                    if (savedAppearance) {
                        savedAppearance.color = currentAppearance?.Color ?? assetSet.Color;
                        savedAppearance.materialID = currentAppearance?.MaterialID ?? null;
                    }
                    else {
                        this._savedAssetSetAppearances.push({
                            id: assetSet.ID,
                            color: currentAppearance?.Color ?? assetSet.Color,
                            materialID: currentAppearance?.MaterialID ?? null,
                        });
                    }
                    return undefined;
                }
                else {
                    const savedAppearance = this._savedAssetSetAppearances.find(entry => entry.id === assetSet.ID);
                    this._savedAssetSetAppearances = this._savedAssetSetAppearances.filter(entry => entry.id !== assetSet.ID); // Remove used entry
                    const hasIncomingMaterial = Object.prototype.hasOwnProperty.call(assetSet, 'MaterialID');

                    return {
                        ...assetSet,
                        Color: savedAppearance ? savedAppearance.color : assetSet.Color,
                        MaterialID: savedAppearance
                            ? savedAppearance.materialID
                            : hasIncomingMaterial
                                ? assetSet.MaterialID
                                : currentAppearance?.MaterialID ?? null,
                    };
                }
            }).filter(assetSet => assetSet !== undefined); // Remove undefined entries (those meant to be removed)

            this._selectedKnowledgeView = {
                ...this._selectedKnowledgeView,
                VersionToken: data.kvDetail.VersionToken,
                Name: data.kvDetail.Name,
                AssetSets: newAssetSets,
                DisplayProperties: data.kvDetail.DisplayProperties ?? this._selectedKnowledgeView.DisplayProperties,
            };
            this._isKnowledgeViewDisplayPriorityDirty = true;

            
        } else {
            this._selectedKnowledgeView = {...this._selectedKnowledgeView, VersionToken: data.kv.VersionToken, Name: data.kv.Name  };
            this._committedKnowledgeView = this._cloneKnowledgeView(this._selectedKnowledgeView);
            this._isKnowledgeViewDirty = false; // This update came from the server, so reset dirty flag
            this._isCameraDirty = false;
            this._isKnowledgeViewDisplayPriorityDirty = false;
        }
    }

    private _resetKnowledgeViewDisplayPriorities() {
        this._displayAllAssetTags = false;
        if (!this._selectedKnowledgeView || !this._committedKnowledgeView ||
            this._selectedKnowledgeView.ID !== this._committedKnowledgeView.ID) return;

        this._selectedKnowledgeView = {
            ...this._selectedKnowledgeView,
            AssetSets: this._committedKnowledgeView.AssetSets?.map(assetSet => ({ ...assetSet })),
        };
        this._savedAssetSetAppearances = [];
        this._isKnowledgeViewDisplayPriorityDirty = false;
    }

    private _cloneKnowledgeView(knowledgeView: IKnowledgeViewDetail): IKnowledgeViewDetail {
        if (!knowledgeView) return null;

        return {
            ...knowledgeView,
            AssetSets: knowledgeView.AssetSets?.map(assetSet => ({ ...assetSet })),
        };
    }

    private _setKnowledgeViewDirty(dirty: boolean) {
        this._isKnowledgeViewDirty = dirty;
        if (!dirty) {
            this._isCameraDirty = false;
            this._isKnowledgeViewDisplayPriorityDirty = false;
        }
    }

    private _renameKnowledgeView(kv: IKnowledgeView) {
        this._selectedKnowledgeView = { ...this._selectedKnowledgeView, VersionToken: kv.VersionToken, Name: kv.Name, Description: kv.Description };
    }

    private _setViewerCameraProperties(yaw: number, pitch: number, fov: number, position: IVector3D) {
        this._viewerCameraProperties = { yaw, pitch, fov, position };
    }

    private _setRangePosition(position: IVector3D) {
        this._rangePosition = position;
    }

    private _exitRangeView() {
        this._rangeViewDistance = 0;
        this._rangePosition = null;
        this._rangeView = null;
        this._overrideFocalDistance = 0;
    }

    private _set3DViewerCameraProperties(position: IVector3D | null, target: IVector3D | null) {
        this._viewer3DCameraProperties = { position, target };
    }

    private _setDisplayObstructedTags(display: boolean) {
        const changed = this._displayObstructedTags !== display;
        this._displayObstructedTags = display;

        if (changed && this._selectedKnowledgeView) {
            this._isKnowledgeViewDirty = true;
        }
    }

    private _setFilterOverlapping360Positions(filter: boolean) {
        const changed = this._filterOverlapping360Positions !== filter;
        this._filterOverlapping360Positions = filter;

        if (changed && this._selectedKnowledgeView) {
            this._isKnowledgeViewDirty = true;
        }
    }

    private _setVisibleAssetTagClasses(assetClassIDs: string[] | null) {
        const changed = !this._stringArraysMatch(this._visibleAssetTagClasses, assetClassIDs);
        this._visibleAssetTagClasses = assetClassIDs ? [...assetClassIDs] : null;

        if (changed && this._selectedKnowledgeView) {
            this._isKnowledgeViewDirty = true;
        }
    }

    private _stringArraysMatch(a: string[] | null, b: string[] | null) {
        if (!a && !b) return true;
        if (!a || !b || a.length !== b.length) return false;

        const sortedA = [...a].sort();
        const sortedB = [...b].sort();
        return sortedA.every((value, index) => value === sortedB[index]);
    }

    private _setCameraDirty(dirty: boolean) {
        this._isCameraDirty= dirty;
    }

    private _changeDisplayMode(mode: DisplayMode, annotationType: AnnotationType, measurementType: MeasurementType, assemblyID: string, selectedAnnotationClassID?: string) {
        this._displayMode = mode;
        this._annotationType = annotationType;
        this._selectedAnnotationClassID = selectedAnnotationClassID ?? null;
        this._measurementType = measurementType;
        this._assemblyCreateID = assemblyID;
    }

    private _changeMeasurementType(measurementType: MeasurementType) {
        this._measurementType = measurementType;
    }

    private _updateMeasurements(updatedMeasurement: Measurement[]) {
        this._measurements = [...updatedMeasurement]; // Create a new array reference
    }

    private _reloadMeasurement(oldID: string, measurement: Measurement) {
        // Find the index of the measurement with the old ID
        const cp = [...this._measurements];
        const index = cp.findIndex(measurement => measurement.id === oldID);

        if (index !== -1) {
            cp[index] = measurement;
        } 
        this._measurements = cp;
    }

    private _setPointCloudLoaded(loaded: boolean) {
        this._pointCloudLoaded = loaded;
    }
    private _setAssemblyCreationTemplate(template: IAssemblyTemplate) {
        this._assemblyCreationTemplate = template;
    }
    private _setOpenPointCloud(open: boolean, resetDirectionSource:boolean) {
        this._isDirectionUpdated = false;
        this._openPointCloud = open;

        if (resetDirectionSource) {
            this._cameraDirection = null;
            this._updateDirectionSource = 0;
        }
    }

    private _setAllowMultiSelect(value: boolean) {
        if (this._allowMultiSelect === value) return;

        this._allowMultiSelect = value;

        // Note, we don't clear the selection here. Any flip of the multi select allowance should also clear selection.
    }

    private _setPointCloudData(pointClouds: BABYLON.Mesh[]) {
        this._pointCloudData = pointClouds;
    }
    private _setViewerDirection(source : any, direction: BABYLON.Vector3) {
        this._cameraDirection = direction;
        this._isDirectionUpdated = true;
        this._updateDirectionSource = source;
    }
    private _setViewerContentTypes(types: string[]) {
        this._viewerContentTypes = types;
    }
    private _setOpenSpeedDial(name: string | null) {
        this._openSpeedDial = name;
    }

    private _addMeasurement(measurement: Measurement) {

        // Create a new array reference
        const exists = this._measurements.some(m => m.id === measurement.id);

        if (!exists) {
            this._measurements = [...this._measurements, measurement];
        }
    }

    private _setAssemblies(assemblies: IAssembly[]) {
        this._assemblies = [...assemblies];
    }

    private _addAssemblies(assemblies: IAssembly[]) {
        // Create a map for the incoming assemblies
        const incomingAssembliesMap = new Map(assemblies.map(a => [a.ID, a]));

        // Update or retain existing assemblies
        this._assemblies = this._assemblies.map(existingAssembly => {
            // If there's a match in the incoming assemblies, replace it
            if (incomingAssembliesMap.has(existingAssembly.ID)) {
                const updatedAssembly = incomingAssembliesMap.get(existingAssembly.ID);
                incomingAssembliesMap.delete(existingAssembly.ID); // Remove from map once updated
                return updatedAssembly!;
            }
            // Otherwise, retain the existing assembly
            return existingAssembly;
        });

        // Add any remaining new assemblies that were not matches
        this._assemblies.push(...Array.from(incomingAssembliesMap.values())); // Use Array.from here
    }

    private _addAssembly(assembly: IAssembly) {
        // Check if the assembly ID already exists
        const existingIndex = this._assemblies.findIndex(a => a.ID === assembly.ID);

        if (existingIndex !== -1) {
            // Replace the existing assembly
            this._assemblies[existingIndex] = assembly;
        } else {
            // Add the new assembly
            this._assemblies = [...this._assemblies, assembly];
        }
    }

    private setAssemblyDirty(dirty: boolean) {
        this._isAssemblyDirty = dirty;
    }
    private _removeAssembly(assemblyID: string) {
        // Remove an assembly
        this._assemblies = this._assemblies.filter(item => item.ID !== assemblyID);

        // clear the selected assembly if it is this one
        if (this._selectedAssembly == assemblyID)
            this._selectedAssembly = null
    }
    private _undoAssembly(assemblies: IAssembly[]) {
        this._assemblies = [...assemblies];
        this._isAssemblyDirty = false;
    }
    private _updateAssembies(assemblies: IAssembly[]) {
        this._assemblies = [...assemblies];
        this._isAssemblyDirty = false;
    }

    private _addAnnotation(annotation: IViewAnnotationTag) {
        this._selectedKnowledgeView.Tags.push(annotation);
        this._tagsVersion++;
    }

    private _editAnnotation(annotation: IViewAnnotationTag) {
        this._tagsVersion++;
    }

    private _deleteAnnotation(annotation: IViewAnnotationTag) {
        var index = this._selectedKnowledgeView.Tags.indexOf(annotation);
        if (index > -1) {
            this._selectedKnowledgeView.Tags.splice(index, 1);
        }

        this._tagsVersion++;
    }

    private _selectAnnotations(data: any) {

        const annotations = data?.annotations ?? [];

        const selectedAnnotationIds = new Set<string>();
        const selectedAssetIds = new Set<string>();

        for (const item of annotations) {
            if (item?.annotationID) {
                selectedAnnotationIds.add(item.annotationID);
            }

            if (item?.assetID) {
                selectedAssetIds.add(item.assetID);
            }
        }

        this._selectedAnnotations = Array.from(selectedAnnotationIds);
        this._selectedAssets = Array.from(selectedAssetIds);

        if (!this.getAllowMultiSelect() &&
            (this._selectedAnnotations.length > 0 || this._selectedAssets.length > 0)) {
            this._selectedHierarchyItems = [];
            this._selectedMeshItem = null;
            this._selectedAssembly = null;
        }

        if (this._selectedAnnotations.length !== 1) {
            this._displayMode = DisplayMode.Navigation;
        }
    }

    private _selectAsset(data: any) {
        if (this._selectedAnnotations.length == 1 && this._selectedAnnotations[0] != data.annotationID) {
            this._displayMode = DisplayMode.Navigation;
        }

        if (data.annotationID == null) {
            this._selectedAnnotations = [];
        }
        else {
            this._selectedAnnotations = [data.annotationID];
        }

        if (data.assetID == null) {
            this._selectedAssets = [];
        }
        else {
            this._selectedAssets = [data.assetID];
        }

        if (!this.getAllowMultiSelect() &&
            (this._selectedAnnotations.length > 0 || this._selectedAssets.length > 0)) {
            this._selectedHierarchyItems = [];
            this._selectedMeshItem = null;
            this._selectedAssembly = null;
        }
    }

    private _selectAssembly(data: any) {
        if (this._selectedAssembly != data) {
            this._displayMode = DisplayMode.Navigation;
        }

        this._selectedAssembly = data;

        // Clear other selections when an assembly is selected
        if (this._selectedAssembly) {
            this._selectedAnnotations = [];
            this._selectedAssets = [];
            this._selectedHierarchyItems = [];
            this._selectedMeshItem = null;
        }
    }

    private _selectHierarchyItem(item: IHierarchyItemDetail) {
        if (item == null) {
            this._selectedHierarchyItems = [];
        }
        else {
            this._selectedHierarchyItems = [item];
        }

        if (this._selectedHierarchyItems.length > 0) {
            this._selectedMeshItem = null;
            this._selectedAssembly = null;

            if (!this.getAllowMultiSelect()) {
                this._selectedAssets = [];
                this._selectedAnnotations = [];
            }
        }
    }

    private _selectHierarchyItems(items: IHierarchyItemDetail[]) {
        this._selectedHierarchyItems = items ?? [];

        if (this._selectedHierarchyItems.length > 0) {
            this._selectedMeshItem = null;
            this._selectedAssembly = null;

            if (!this.getAllowMultiSelect()) {
                this._selectedAssets = [];
                this._selectedAnnotations = [];
            }
        }
    }

    private _selectMeshItem(item: any) {
        this._selectedMeshItem = item;

        // Clear other selections if a mesh item is selected
        if (this._selectedMeshItem) {
            this._selectedHierarchyItems = [];
            this._selectedAssets = [];
            this._selectedAnnotations = [];
            this._selectedAssembly = null;
        }
    }

    private _selectAssetSet(item: IAssetSet) {
        this._selectedAssetSet = item;
    }

    private _updateAssemblyBounds(data: IAssemblyBounds) {
        this._selectedAssemblyBounds = data;
    }

    private _updateAnnotation(annotation: IAnnotationDisplayItem) {
        this._updatedAnnotation = annotation;
    }

    private _updateAssembly(assemblyID: string, position: IVector3D, rotation: IVector3D, scale: IVector3D) {
        // Find the assembly in the _assemblies array
        const index = this._assemblies.findIndex(assembly => assembly.ID === assemblyID);

        if (index !== -1) {
            // Create a new assembly object with updated properties
            const updatedAssembly = {
                ...this._assemblies[index],
                Position: position,
                Orientation:rotation,
                InstanceScale:scale,
            };

            // Create a new array reference with the updated assembly
            this._assemblies = [
                ...this._assemblies.slice(0, index),
                updatedAssembly,
                ...this._assemblies.slice(index + 1),
            ];
            this._isAssemblyDirty = true;
        } else {
            console.warn(`Assembly with ID ${assemblyID} not found.`);
        }
    }
    private _setDepthMapLoaded(tags: IAnnotationDisplayItem[]) {
        this._depthMapLoaded = true;
        this._occludeAnnotationIDs(tags.map(ann => ann.ID));
    }

    private _setMiniMapOpen(open: boolean) {
        this._isMiniMapOpen = open;
    }
    private _setMiniMapPositions(positions: PositionDataModel) {
        this._miniMapPositions = positions;
    }

    private _updateViewerSetting(name: string, value: any) {
        if (!name) return;

        const current = this._viewerRuntimeSettings[name];

        if (current === value) return;

        this._viewerRuntimeSettings = {
            ...this._viewerRuntimeSettings,
            [name]: value
        };
    }

    getViewerRuntimeSetting<T = any>(name: string, defaultValue?: T): T {
        const v = this._viewerRuntimeSettings?.[name];
        return (v === undefined ? defaultValue : v) as T;
    }

    getViewerRuntimeSettings(): Record<string, any> {
        return this._viewerRuntimeSettings;
    }

    private setMiniMapSize(size: any) {
        this._miniMapSize = size;
    }
    private setMiniMapFullScreen(mode: boolean) {
        this._miniMapFullScreen = mode;
    }
    private _occludeAnnotationIDs(annotationIDs: string[]) {
        this._occludedAnnotations = annotationIDs;
    }

    private _focusAnnotation(annotation: IAnnotationDisplayItem) {
        this._focusedAnnotation = annotation;

        if (this._focusedAnnotation && !this._contentLayers.assetTags) {
            this._visibleAssetTagClasses = null;
            this._contentLayers = {
                ...this._contentLayers,
                assetTags: true
            };
        }
    }

    private _focusHierarchyItem(id: string) {
        this._focusedHierarchyItemID = {id};

        if (this._focusedHierarchyItemID && !this._contentLayers.assets) {
            this._contentLayers = {
                ...this._contentLayers,
                assets: true
            };
        }
    }

    private _focusMeasurement(measurement: Measurement) {
        this._focusedMeasurement = measurement;
    }

    private _loadSettings(data: IFacilitySettings) {
        this._settings = {...this._settings} // Create a copy of the current settings

        const compassSetting = data.ViewerSettings?.find(s => s.Name == PanoViewerConstants.PanoCompass)?.Value;
        if (compassSetting) {
            this._settings.PanoCompassVisible = (compassSetting == PanoViewerConstants.Show);
        }

        const scanSphereColorSetting = data.ViewerSettings?.find(s => s.Name == PanoViewerConstants.PanoScanSphereColor)?.Value;
        if (scanSphereColorSetting) {
            this._settings.PanoScanSphereColor = scanSphereColorSetting;
        }

        const imageSphereColorSetting = data.ViewerSettings?.find(s => s.Name == PanoViewerConstants.PanoImageSphereColor)?.Value;
        if (imageSphereColorSetting) {
            this._settings.PanoImageSphereColor = imageSphereColorSetting;
        }

        const tagVolumnBoxColorColorSetting = data.ViewerSettings?.find(s => s.Name == PanoViewerConstants.TagVolumeBoxColor)?.Value;
        if (tagVolumnBoxColorColorSetting) {
            this._settings.TagVolumeBoxColor = tagVolumnBoxColorColorSetting;
        }

        const unitMeasurement = data.ViewerSettings?.find(s => s.Name == PanoViewerConstants.UnitMeasurement)?.Value;
        if (tagVolumnBoxColorColorSetting) {
            this._settings.UnitMeasurement = unitMeasurement;
        }

        const zoomSliderSetting = data.ViewerSettings?.find(s => s.Name == PanoViewerConstants.ZoomSlider)?.Value;
        if (zoomSliderSetting) {
            this._settings.ZoomSliderVisible = (zoomSliderSetting == PanoViewerConstants.On);
        }

        const edgeShaderSetting = data.ViewerSettings?.find(s => s.Name == PanoViewerConstants.EdgeShader)?.Value;
        if (edgeShaderSetting) {
            this._settings.EdgeShader = (edgeShaderSetting == PanoViewerConstants.On);
        }

        const backgroundColorSetting = data.ViewerSettings?.find(s => s.Name == PanoViewerConstants.BackgroundColor)?.Value;
        if (backgroundColorSetting) {
            this._settings.BackgroundColor = backgroundColorSetting;
        }

        const MeasurementColorSetting = data.ViewerSettings?.find(s => s.Name == PanoViewerConstants.MeasurementColor)?.Value;
        if (MeasurementColorSetting) {
            this._settings.MeasurementColor = MeasurementColorSetting;
        }

        const MemoryProfileSetting = data.ViewerSettings?.find(s => s.Name == PanoViewerConstants.MemoryProfile)?.Value;
        if (MemoryProfileSetting) {
            this._settings.MemoryProfile = MemoryProfileSetting;
        }

        const viewerMemoryBudgetSetting = data.ViewerSettings?.find(s => s.Name == PanoViewerConstants.ViewerMemoryBudget)?.Value;
        if (viewerMemoryBudgetSetting) {
            this._settings.ViewerMemoryBudget = viewerMemoryBudgetSetting;
        }

        const meshResolutionSetting = data.ViewerSettings?.find(s => s.Name == PanoViewerConstants.MeshResolution)?.Value;
        if (meshResolutionSetting) {
            this._settings.MeshResolution = meshResolutionSetting;
        } else {
            switch ((this._settings.MemoryProfile || "").toLowerCase()) {
                case "low":
                    this._settings.MeshResolution = "Low";
                    break;
                case "medium":
                    this._settings.MeshResolution = "Medium";
                    break;
                default:
                    this._settings.MeshResolution = "High";
                    break;
            }
        }

        const cameraTypeSetting = data.ViewerSettings?.find(s => s.Name == PanoViewerConstants.CameraType)?.Value;
        if (cameraTypeSetting) {
            this._settings.CameraType = cameraTypeSetting;
        }

        const showCrosshairSetting = data.ViewerSettings?.find(s => s.Name == PanoViewerConstants.ShowCrosshair)?.Value;
        if (showCrosshairSetting) {
            this._settings.ShowCrosshair = (showCrosshairSetting == PanoViewerConstants.On);
        }

        const rotationSensitivity = data.ViewerSettings?.find(s => s.Name == PanoViewerConstants.RotationSensitivity)?.Value;
        if (rotationSensitivity) {
            this._settings.RotationSensitivity = rotationSensitivity;
        }

        const velocitySensitivity = data.ViewerSettings?.find(s => s.Name == PanoViewerConstants.VelocitySensitivity)?.Value;
        if (velocitySensitivity) {
            this._settings.VelocitySensitivity = velocitySensitivity;
        }

        const invertYSetting = data.ViewerSettings?.find(s => s.Name == PanoViewerConstants.InvertY)?.Value;
        if (invertYSetting) {
            this._settings.InvertY = (invertYSetting == PanoViewerConstants.On);
        }
    }

    private _setAnnotationEditorMode(mode: boolean) {
        this._annotationTextEditorMode = mode;
    }
    private _resetSelectedAnnotation() {
        this._selectedAssets = [];
        this._selectedAnnotations = [];
        this._updatedAnnotation = null;
    }
    private _setAttributesInformation(data: IAttributeSetValue[]) {
        this._attributesInformation = data;
    }
    private _editAssetTag(tag: IAssetTag) {
        this._editedAssetTag = tag;
    }

    private _setAssetTagFilter(filterData: IAssetFilter, filteredAssetIDs: string[]) {
        this._assetTagFilter = filterData;
        this._filteredAssets = filteredAssetIDs;
    }
    private _setSelectedAssemblyTemplate(assemblyTemplate: IAssemblyTemplate) {
        this._selectedAssemblyTemplate = assemblyTemplate;
    }
    private _changeAssetPanelVisibility(isVisible: boolean) {
        this._assetPanelVisibility = isVisible;
        saveAssetInformationPanelOpen(isVisible);
    }

    private _setContentLayers(next: ContentLayers) {
        const nextContentLayers = { ...defaultContentLayers, ...next };
        const changed = (Object.keys(nextContentLayers) as (keyof ContentLayers)[])
            .some(layer => this._contentLayers[layer] !== nextContentLayers[layer]);

        this._contentLayers = nextContentLayers;

        if (changed && this._selectedKnowledgeView) {
            this._isKnowledgeViewDirty = true;
        }
    }

    // ViewerStore API methods
    getUnitInformation() : IUnitInformation {
        const foundUnit = this._unitConversions.find(unit => unit.Unit === this._settings.UnitMeasurement);
        return foundUnit || this._unitConversions[0];
    }

    getPointCloudData(): BABYLON.Mesh[] {
        return this._pointCloudData;
    }

    getDisplayObstructedTags(): boolean {
        return this._displayObstructedTags;
    }

    getFilterOverlapping360Positions(): boolean {
        return this._filterOverlapping360Positions;
    }

    getVisibleAssetTagClasses(): string[] | null {
        return this._visibleAssetTagClasses ? [...this._visibleAssetTagClasses] : null;
    }

    convertToUserUnit(baseUnit : number, roundToNearestMM : boolean = true) {

        var rounded = roundToNearestMM ? Math.round(baseUnit * 1000) : baseUnit * 1000;

        var userUnit = (rounded * this.getUnitInformation().Conversion).toFixed(this.getUnitInformation().Precision);

        return userUnit;
    }
    
    convertToMeters(userUnit: number, roundToNearestMM: boolean = true) {
        const conversionToMeters = this.getUnitInformation().ConversionToMM / 1000; // Direct conversion to meters
        const meters = userUnit * conversionToMeters;

        return roundToNearestMM ? Math.round(meters * 1000) / 1000 : meters; // Optionally round to the nearest millimeter
    }

    getSelectedObjects(): Interfaces.Interfaces.ISelectedObjectInfo[] {
        const selected: Interfaces.Interfaces.ISelectedObjectInfo[] = [];

        for (const annotationId of this._selectedAnnotations ?? []) {
            if (!annotationId) continue;

            selected.push({
                id: annotationId,
                objectType: Enumerations.PickObjectType.Annotation
            });
        }

        for (const hierarchyItem of this._selectedHierarchyItems ?? []) {
            if (!hierarchyItem?.ID) continue;

            selected.push({
                id: hierarchyItem.ID,
                objectType: Enumerations.PickObjectType.Object
            });
        }

        if (this._selectedMeshItem?.ID) {
            selected.push({
                id: this._selectedMeshItem.ID,
                objectType: Enumerations.PickObjectType.Mesh
            });
        }

        return selected;
    }

    refreshFacilityViews(views?: IView360Detail[] | null) {
        if (!views?.length) {
            return false;
        }

        const viewsById = new Map(views.map(view => [view.ID, view]));
        let changed = false;

        if (this._selectedViewImage?.ID) {
            const refreshedSelectedView = viewsById.get(this._selectedViewImage.ID);
            if (refreshedSelectedView && refreshedSelectedView !== this._selectedViewImage) {
                this._selectedViewImage = refreshedSelectedView;
                changed = true;
            }
        }

        if (this._rangeView?.ID) {
            const refreshedRangeView = viewsById.get(this._rangeView.ID);
            if (refreshedRangeView && refreshedRangeView !== this._rangeView) {
                this._rangeView = refreshedRangeView;
                changed = true;
            }
        }

        if (changed) {
            this.emitChange();
        }

        return changed;
    }

    getSelectedViewImage() {
        return this._selectedViewImage;
    }
    getViewOntolog() {
        return this._viewOntology;
    }
    getCameraDirection() {
        return this._cameraDirection;
    }
    getContentTypes() {
        return this._viewerContentTypes;
    }
    getContentLayers(): ContentLayers {
        return this._contentLayers;
    }

    getDisplayAllAssetTags(): boolean {
        return this._displayAllAssetTags;
    }
    getOpenSpeedDial() {
        return this._openSpeedDial;
    }

    getFocusedAnnotation() {
        return this._focusedAnnotation;
    }

    getFocusedHierarchyItemID() {
        return this._focusedHierarchyItemID;
    }

    isMiniMapOpen() {
        return this._isMiniMapOpen;
    }
    miniMapPositions() {
        return this._miniMapPositions;
    }
    miniMapSize() {
        return this._miniMapSize;
    }
    miniMapFullScreen() {
        return this._miniMapFullScreen;
    }

    getFocusedMeasurement() {
        return this._focusedMeasurement;
    }

    getSettings() {
        return this._settings;
    }

    getDefaultSettings() {
        return this._defaultSettings;
    }

    getSelectedAsset() {
        return (this._selectedAssets.length == 1 &&
            this._selectedMeshItem == null &&
            this._selectedHierarchyItems.length == 0 &&
            this._selectedAssembly == null)
            ? this._selectedAssets[0] : null;
    }

    getSelectedAnnotation() {
        return (this._selectedAnnotations.length == 1 &&
            this._selectedMeshItem == null &&
            this._selectedHierarchyItems.length == 0 &&
            this._selectedAssembly == null)
            ? this._selectedAnnotations[0] : null;
    }

    getSelectedAnnotations() {

        return this._selectedAnnotations;
    }

    getSelectedAssembly() {
        return this._selectedAssembly;
    }

    getSelectedAssemblyBounds() {
        return this._selectedAssemblyBounds;
    }
    getUpdatedAnnotation() {
        return this._updatedAnnotation;
    }

    getFilteredAssets() {
        return this._filteredAssets;
    }

    getOccludedAnnotations() {
        return this._occludedAnnotations;
    }

    getSelectedKnowledgeView() {
        return this._selectedKnowledgeView;
    }

    getCommittedKnowledgeView() {
        return this._committedKnowledgeView;
    }

    getSavedKnowledgeView() {
        return this._savedKnowledgeView;
    }

    getViewerCameraProperties() {
        return this._viewerCameraProperties;
    }

    getRangePosition() {
        return this._rangePosition;
    }

    getRangeView() {
        return this._rangeView;
    }

    isInRangeView() {
        return !!this._rangeViewDistance;
    }

    getViewer3DCameraProperties() {
        return this._viewer3DCameraProperties;
    }

    getTagsVersion() {
        return this._tagsVersion;
    }

    getKVContentVersion() {
        return this._kvContentVersion;
    }

    getKnowledgeViewOpenVersion() {
        return this._knowledgeViewOpenVersion;
    }

    getMeasurements() {
        return this._measurements;
    }
    getAssemblies() {
        return this._assemblies;
    }
    getMeasurementType() {
        return this._measurementType;
    }
    getAssemblyCreationID() {
        return this._assemblyCreateID;
    }
    getAssemblyCreationTemplate() {
        return this._assemblyCreationTemplate;
    }
    isDepthMapLoaded() {
        return this._depthMapLoaded;
    }

    isPanoViewerVisible() {
        return (this._selectedViewImage) ? true : false;
    }

    isOpenPointCloud() {
        return this._openPointCloud;
    }

    isPointCloudLoaded() {
        return this._pointCloudLoaded;
    }

    isDirectionUpdated() {
        return this._isDirectionUpdated;
    }

    getAllowMultiSelect() {
        return this._allowMultiSelect;
    }

    getDirectionUpdateSource() {
        return this._updateDirectionSource;
    }
    getDisplayMode () {
        return this._displayMode;
    }

    getAnnotationType() {
        return this._annotationType;
    }

    getSelectedAnnotationClassID() {
        return this._selectedAnnotationClassID;
    }

    isCameraDirty() {
        return this._isCameraDirty;
    }
    isKnowledgeViewDisplayPriorityDirty() {
        return this._isKnowledgeViewDisplayPriorityDirty;
    }
    isKnowledgeViewDirty() {
        return this._isKnowledgeViewDirty
            || this._isKnowledgeViewDisplayPriorityDirty
            || this._isCameraDirty;
    }

    getKnowledgeViewEditID(): string | null {
        return this._knowledgeViewEditID === this._selectedKnowledgeView?.ID ? this._knowledgeViewEditID : null;
    }

    isEditingKnowledgeView(): boolean {
        return !!this.getKnowledgeViewEditID();
    }

    isKnowledgeViewEditBusy(): boolean {
        return this._knowledgeViewEditBusy;
    }

    getAnnotationEditorMode() {
        return this._annotationTextEditorMode;
    }
    getAttributesInformation() {
        return this._attributesInformation;
    }
    getOverrideFocalDistance() {
        return this._overrideFocalDistance;
    }
    getEditedAssetTag() {
        return this._editedAssetTag;
    }
    getAssetTagsFilter() {
        return this._assetTagFilter;
    }
    getAssetPanelVisibility() {
        return this._assetPanelVisibility;
    }
    getSelectedAssemblyTemplate() {
        return this._selectedAssemblyTemplate;
    }
    isAssemblyDirty() {
        return this._isAssemblyDirty;
    }
    is3DViewerVisible() {
        return this._show3DViewer;
    }
    getLODData() {
        return this._lodData;
    }
    getLODDistanceThreshold() {
        return this._rangeViewDistance;
    }

    getSelectedHierarchyItems(): IHierarchyItemDetail[] {
        return this._selectedHierarchyItems;
    }

    getSelectedHierarchyItem(): IHierarchyItemDetail | null {
        return (this._selectedHierarchyItems.length == 1 &&
            this._selectedMeshItem == null &&
            this._selectedAssets.length == 0 &&
            this._selectedAnnotations.length == 0 &&
            this._selectedAssembly == null)
            ? this._selectedHierarchyItems[0] : null;
    }

    getSelectedMeshItem() {
        return this._selectedMeshItem;
    }
    getSelectedAssetSet() {
        return this._selectedAssetSet;
    }
    
    // EventEmitter API methods

    emitChange() {
        this.emit(ViewerStore.CHANGE_EVENT);
    }

    addChangeListener(callback: any) {
        this.on(ViewerStore.CHANGE_EVENT, callback);
    }

    removeChangeListener(callback: any) {
        this.removeListener(ViewerStore.CHANGE_EVENT, callback);
    }
}

const viewerStore = new ViewerStore();

export default viewerStore;
